import type { FindingStatusEventOut } from '@/api/types';
import { REASON_CODE_META } from '@/lib/reason-codes';

/** One step in a finding's disposition trail, ready to render as a
 *  chip: "brian approved" · "rejected — invoice shows no tax". */
export interface DispositionSegment {
  toStatus: string;
  verb: string;
  actor: string;
  note: string | null;
}

const VERBS: Record<string, string> = {
  approved: 'approved',
  rejected: 'rejected',
  needs_review: 'flagged for review',
  pending: 'reset to pending',
};

// The real dispositions a finding can arrive from — as opposed to the
// `pending` birth state, which isn't worth a leading trail segment.
const PRIOR_DISPOSITIONS = new Set([
  'approved',
  'rejected',
  'needs_review',
]);

/** Turn the raw event log into a readable left-to-right trail. The
 *  note prefers the first structured reason code's label (so
 *  `no_tax_charged` reads "No tax charged"), falling
 *  back to the free-text reason. Actor defaults to "system" for
 *  non-practitioner transitions (carry-forward sweep, materiality
 *  floor).
 *
 *  When the earliest event's `from_status` is a real prior disposition
 *  (i.e. the finding was already approved/rejected/needs_review before
 *  the event log started recording — true for every finding
 *  dispositioned pre-INP-457), a synthetic leading segment is prepended
 *  for that state. Without it, resolving the approved+no-tax conflict on
 *  a legacy finding would render just "rejected" and drop the "approved
 *  →" context the whole trail exists to show. The synthetic segment has
 *  no actor (the prior action predates the log). */
export function dispositionTrail(events: FindingStatusEventOut[]): DispositionSegment[] {
  const segments = events.map((e) => {
    const code = e.reason_codes[0];
    const note = code ? REASON_CODE_META[code].label : (e.reason?.trim() || null);
    return {
      toStatus: e.to_status,
      verb: VERBS[e.to_status] ?? e.to_status,
      actor: e.is_system ? 'system' : (e.actor_alias ?? 'system'),
      note,
    };
  });

  const first = events[0];
  if (first && first.from_status && PRIOR_DISPOSITIONS.has(first.from_status)) {
    segments.unshift({
      toStatus: first.from_status,
      verb: VERBS[first.from_status] ?? first.from_status,
      actor: '',
      note: null,
    });
  }
  return segments;
}
